import React, { useState } from 'react';
import { Package, AlertTriangle, TrendingUp, TrendingDown, Clock, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

function StockOverview({ data, loading }) {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-30" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1 inline" />
      : <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };

  const sortedStock = data ? [...data].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'name':
        aValue = a.variant?.product?.name || '';
        bValue = b.variant?.product?.name || '';
        break;
      case 'sku':
        aValue = a.variant?.sku || '';
        bValue = b.variant?.sku || '';
        break;
      case 'totalStock':
        aValue = a.totalStock;
        bValue = b.totalStock;
        break;
      case 'totalCuring':
        aValue = a.totalCuring;
        bValue = b.totalCuring;
        break;
      case 'totalSold':
        aValue = a.totalSold;
        bValue = b.totalSold;
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string') {
      const comparison = aValue.localeCompare(bValue, 'da-DK');
      return sortDirection === 'asc' ? comparison : -comparison;
    }

    if (aValue === bValue) return 0;
    const comparison = aValue < bValue ? -1 : 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  }) : [];

  const lowStock = sortedStock.filter(item => item.available <= 10);

  return (
    <div className="space-y-6">
      {/* Stock Alerts */}
      {lowStock.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
            <h3 className="text-lg font-medium text-yellow-800">
              Lavt lager ({lowStock.length} produkter)
            </h3>
          </div>
        </div>
      )}

      {/* Stock Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Lageroversigt</h2>
        </div>

        <div className="overflow-x-auto rounded-b-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('name')}
                >
                  Produkt {getSortIcon('name')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('sku')}
                >
                  SKU {getSortIcon('sku')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensioner
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('totalStock')}
                >
                  Total på lager {getSortIcon('totalStock')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('totalCuring')}
                >
                  Hærder {getSortIcon('totalCuring')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('totalSold')}
                >
                  Antal Solgt {getSortIcon('totalSold')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedStock.map((item) => {
                const isBackOrder = item.available < 0;
                const isLowStock = item.available > 0 && item.available <= 10;
                const stockLevel = item.available > 50 ? 'high' : item.available > 10 ? 'medium' : item.available > 0 ? 'low' : 'backorder';
                
                return (
                  <tr key={item.id} className={
                    isBackOrder ? 'bg-red-50' : 
                    isLowStock ? 'bg-yellow-50' : ''
                  }>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.variant?.product?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.variant?.product?.category}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.variant?.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.variant?.dimensions?.length}×{item.variant?.dimensions?.width}×{item.variant?.dimensions?.height}cm
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-lg font-semibold ${
                          stockLevel === 'high' ? 'text-green-600' :
                          stockLevel === 'medium' ? 'text-yellow-600' :
                          stockLevel === 'low' ? 'text-red-600' :
                          'text-red-700'
                        }`}>
                          {item.totalStock}
                        </span>
                        {stockLevel === 'high' && <TrendingUp className="ml-1 h-4 w-4 text-green-600" />}
                        {stockLevel === 'low' && <TrendingDown className="ml-1 h-4 w-4 text-red-600" />}
                        {stockLevel === 'backorder' && <Clock className="ml-1 h-4 w-4 text-red-700" />}
                      </div>
                      {isBackOrder && (
                        <div className="text-xs text-red-600 font-medium mt-1">
                          Back-order: {Math.abs(item.available)} stk
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.totalCuring}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.totalSold}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        stockLevel === 'high' ? 'bg-green-100 text-green-800' :
                        stockLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        stockLevel === 'low' ? 'bg-red-100 text-red-800' :
                        'bg-red-200 text-red-900'
                      }`}>
                        {stockLevel === 'high' ? 'Højt lager' :
                         stockLevel === 'medium' ? 'Medium lager' : 
                         stockLevel === 'low' ? 'Lavt lager' :
                         'Back-order'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StockOverview;